import { Typography } from "@/components/atoms";
import { DirhamIcon } from "@/components/atoms/icons";
import { useTranslation, useUIColors } from "@/core/hooks";
import { useI18nContext } from "@/core/providers/I18nProvider";
import { hasNumericValue, hasTextValue } from "@/core/utils/fieldAvailability";
import React from "react";
import { Platform, StyleSheet, View } from "react-native";

export interface PropertyPriceBadgeProps {
  /** Omit / 0 / null hides the price row instead of showing a placeholder. */
  price?: number | null;
  handoverDate: string | null;
}

const getHandoverQuarterLabel = (dateStr: string | null): string | null => {
  // API sends this already formatted as a quarter label (e.g. "Q4 2028") — use as-is.
  return hasTextValue(dateStr) ? dateStr.trim() : null;
};

export function PropertyPriceBadge({ price, handoverDate }: Readonly<PropertyPriceBadgeProps>) {
  const c = useUIColors();
  const { t } = useTranslation();
  const { isRTL } = useI18nContext();
  const handoverLabel = getHandoverQuarterLabel(handoverDate);
  const showPrice = hasNumericValue(price);

  if (!showPrice && !handoverLabel) {
    return null;
  }

  return (
    <View style={[styles.container, { backgroundColor: c.backgroundLevel2 }]}>
      {/* Mobile: plain row, native auto-mirrors under RTL. Web: unchanged —
          keeps its original manual reverse. */}
      <View
        style={[
          styles.row,
          Platform.OS === "web" && isRTL && { flexDirection: "row-reverse" },
        ]}
      >
        {showPrice && (
          <View style={[styles.valueRow, isRTL && styles.valueRowRTL]}>
            <DirhamIcon size={21} color={c.fontPrimary} />
            <Typography variant="headline" color="primary">
              {price.toLocaleString()}
            </Typography>
          </View>
        )}
        {handoverLabel && (
          <Typography variant="footnote" color="tertiary">
            {t("propertyDetails.handover")} {handoverLabel}
          </Typography>
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  row: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  valueRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    // Always immune to native RTL auto-mirror — direction is controlled
    // explicitly below (valueRowRTL) instead.
    direction: "ltr",
  },
  // Arabic: price value before the Dirham icon (reversed from icon-then-price).
  valueRowRTL: {
    flexDirection: "row-reverse",
  },
});

export default PropertyPriceBadge;
